import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Panel, StatusDot } from "@/components/kit";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, AlertTriangle, Zap } from "lucide-react";

const COLORS = {
    ok: "#39FF14",
    no_credit: "#FF003C",
    quota: "#FF003C",
    bad_key: "#FF003C",
    rate_limit: "#FFD700",
    timeout: "#FFD700",
    error: "#FFD700",
    unknown: "#7A7A7A",
    unconfigured: "#7A7A7A",
};

const EngineCard = ({ e }) => {
    const color = COLORS[e.status] || "#7A7A7A";
    return (
        <div
            data-testid={`engine-card-${e.id}`}
            className="border border-border bg-card p-4"
            style={{ borderLeft: `3px solid ${color}` }}
        >
            <div className="flex items-start justify-between gap-2">
                <div>
                    <div className="text-xs uppercase tracking-wider">{e.name}</div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">{e.vendor}</div>
                </div>
                <span className="flex shrink-0 items-center gap-1.5 text-[10px] uppercase" style={{ color }}>
                    <StatusDot color={color} pulse={e.status === "ok"} />
                    <span data-testid={`engine-status-${e.id}`}>{e.status_label}</span>
                </span>
            </div>

            <div className="mt-3 text-[10px] leading-relaxed text-muted-foreground">
                <div>
                    <span className="text-foreground/70">Costo:</span> {e.cost}
                </div>
                {e.in_cascade ? (
                    <div>
                        <span className="text-foreground/70">Cascada:</span> intento #{e.order}
                    </div>
                ) : (
                    <div className="text-[#FFD700]">No conectado a la cascada</div>
                )}
                {e.detail && (
                    <div className="mt-2 border-t border-border pt-2" data-testid={`engine-detail-${e.id}`}>
                        {e.detail}
                    </div>
                )}
                {["no_credit", "quota", "bad_key"].includes(e.status) && (
                    <div className="mt-2 flex items-start gap-1 text-[#FF003C]">
                        <ExternalLink size={10} className="mt-0.5 shrink-0" /> {e.topup}
                    </div>
                )}
            </div>
        </div>
    );
};

export default function QuotaPanel() {
    const qc = useQueryClient();
    const { data } = useQuery({
        queryKey: ["engines-status"],
        queryFn: async () => (await api.get("/engines/status")).data,
        refetchInterval: 15000,
    });
    const reset = useMutation({
        mutationFn: async () => (await api.post("/engines/reset")).data,
        onSuccess: () => {
            toast.success("Motores reiniciados — la cascada volverá a intentarlos");
            qc.invalidateQueries({ queryKey: ["engines-status"] });
        },
        onError: () => toast.error("No se pudo revalidar los motores"),
    });

    const { data: freeMode } = useQuery({
        queryKey: ["free-mode"],
        queryFn: async () => (await api.get("/governance/free-mode")).data,
        refetchInterval: 20000,
    });
    const toggleFree = useMutation({
        mutationFn: async (enabled) => (await api.post("/governance/free-mode", { enabled })).data,
        onSuccess: (d) => {
            toast.success(d.enabled
                ? "Modo 100% GRATIS activado — todos los videos se harán sin motores de pago"
                : "Modo gratis desactivado — vuelve a usar los motores de pago");
            qc.invalidateQueries({ queryKey: ["free-mode"] });
        },
        onError: () => toast.error("No se pudo cambiar el modo"),
    });
    const isFree = !!freeMode?.enabled;

    const list = data?.engines || [];

    return (
        <Panel
            title="Panel de cuotas · motores de video"
            testId="panel-quotas"
            right={
                <button
                    data-testid="engines-revalidate-btn"
                    onClick={() => reset.mutate()}
                    disabled={reset.isPending}
                    className="flex items-center gap-1.5 border border-border px-2 py-1 text-[10px] uppercase tracking-wider transition-colors duration-150 hover:border-primary hover:text-primary disabled:opacity-40"
                >
                    <RefreshCw size={11} className={reset.isPending ? "animate-spin" : ""} /> Revalidar
                </button>
            }
        >
            <div
                data-testid="free-mode-toggle-row"
                className="mb-4 flex items-center justify-between gap-3 border p-3"
                style={{ borderColor: isFree ? "#39FF14" : "#2a2a2a", background: isFree ? "rgba(57,255,20,0.06)" : "transparent" }}
            >
                <div className="flex items-start gap-2 text-[11px]">
                    <Zap size={14} className="mt-0.5 shrink-0" style={{ color: isFree ? "#39FF14" : "#7A7A7A" }} />
                    <div>
                        <div className="uppercase tracking-wider" style={{ color: isFree ? "#39FF14" : "inherit" }}>
                            Modo 100% GRATIS {isFree ? "· ACTIVO" : ""}
                        </div>
                        <div className="mt-0.5 text-[10px] text-muted-foreground">
                            Todos los videos se generan con el motor gratis (imágenes + narración + FFmpeg).
                            No usa fal.ai, Veo, Sora ni MiniMax. Ideal cuando te quedas sin crédito.
                        </div>
                    </div>
                </div>
                <button
                    data-testid="free-mode-toggle-btn"
                    onClick={() => toggleFree.mutate(!isFree)}
                    disabled={toggleFree.isPending}
                    className="shrink-0 border px-3 py-1.5 text-[10px] uppercase tracking-wider transition-colors duration-150 disabled:opacity-40"
                    style={{
                        borderColor: isFree ? "#39FF14" : "#555",
                        color: isFree ? "#39FF14" : "#ccc",
                        background: isFree ? "rgba(57,255,20,0.1)" : "transparent",
                    }}
                >
                    {isFree ? "Desactivar" : "Activar gratis"}
                </button>
            </div>
            {data && !data.any_available && (
                <div
                    data-testid="engines-none-available"
                    className="mb-4 flex items-start gap-2 border border-[#FF003C] bg-[#FF003C]/10 p-3 text-[11px] text-[#FF003C]"
                >
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                    <span>
                        Ningún motor de video tiene saldo. La producción se detendrá al instante en vez de
                        gastar 15 min intentando. Recarga uno y pulsa «Revalidar».
                    </span>
                </div>
            )}
            <div className="grid grid-cols-1 gap-px bg-border sm:grid-cols-2 lg:grid-cols-5">
                {list.map((e) => (
                    <EngineCard key={e.id} e={e} />
                ))}
                {!list.length && <div className="p-4 text-xs text-muted-foreground">Cargando motores…</div>}
            </div>
            <div className="mt-3 text-[10px] text-muted-foreground">
                El estado proviene del último intento REAL de render (los proveedores no exponen saldo
                gratis). Un veredicto de «sin saldo» caduca en {data?.stale_hours ?? 6} h.
            </div>
        </Panel>
    );
}
